import { ClientEvents } from 'common/events/ClientEvents';
import { ArrayType } from 'common/TypeUtilities';
import { QueryParams } from 'common/urls/QueryParams';
import { Slugs } from 'common/urls/Slugs';
import { map } from 'lodash';
import { useMemo } from 'react';
import { useRouteMatch } from 'react-router';
import { ModalReviewFragment } from 'src/gqlReactTypings.generated.d';
import { DynamicImage } from 'src/shared/design-system/DynamicImage';
import { useHistory } from 'src/shared/hooks/useHistory';
import { useLocation } from 'src/shared/hooks/useLocation';
import { useTracker } from 'src/shared/hooks/useTracker';
import { Routes } from 'src/shared/Routes';
import { ifNotProp } from 'src/shared/styled-utils/ifNotProp';
import { getParam, getSlug, getSluggedPath } from 'src/shared/utils/RouteUtilities';
import styled, { css } from 'styled-components';
import { BorderRadius, Colors, FontSize, FontWeight, LineHeight, Spacing } from 'web-common/src/shared/styles';
import { CarouselLineItemChips } from './CarouselLineItemChips';

const IMAGE_SIZE = 120;

const LineItemImage = styled(DynamicImage)`
  border-radius: ${BorderRadius.M};
  margin-bottom: ${Spacing.HALF};
`;

const LineItemName = styled.p`
  font-size: ${FontSize.XS};
  font-weight: ${FontWeight.SEMI_BOLD};
  line-height: ${LineHeight.XXS};
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const StyledCarouselLineItem = styled.div<{ available: boolean }>`
  align-self: flex-start;
  cursor: pointer;
  margin-right: ${Spacing.SINGLE};
  width: ${IMAGE_SIZE}px;

  ${ifNotProp(
    'available',
    css`
      pointer-events: none;

      ${LineItemName} {
        color: ${Colors.GRAY_DARK_10};
      }

      ${LineItemImage} {
        opacity: 0.3;
      }
    `
  )};
`;

interface CarouselLineItemProps {
  available: boolean;
  lineItem: ArrayType<ModalReviewFragment['dishes']>;
  shefId: string;
}

export const CarouselLineItem = ({ available, lineItem, shefId }: CarouselLineItemProps): JSX.Element => {
  const location = useLocation();
  const { name, imageUrl, rating, slug } = lineItem;
  const ratingPercent = rating?.ratingPercent;
  const tags = map<{ tag: string }>(rating?.tags ?? [], 'tag');
  const history = useHistory();

  const match = useRouteMatch();
  const querySlug = getSlug(match, Slugs.QUERY);
  const idSlug = getSlug(match, Slugs.ID);
  const exploreSegmentId = getParam(location, QueryParams.EXPLORE_SEGMENT_ID);
  const tracker = useTracker();

  const slugs = useMemo(
    () => [
      { slug: Slugs.ID, value: idSlug },
      { slug: Slugs.QUERY, value: querySlug },
      { slug: Slugs.NAME, value: slug },
    ],
    [idSlug, querySlug, slug]
  );

  const foodItemPath = getSluggedPath(
    Routes.CONSUMER_ORDER_FOOD_ITEM,
    slugs,
    exploreSegmentId
      ? {
          [QueryParams.EXPLORE_SEGMENT_ID]: exploreSegmentId,
        }
      : undefined
  );

  const handleClick = () => {
    tracker.track(ClientEvents.REVIEW_FOOD_ITEM_IMAGE_CLICK, {
      shefId,
      foodItemSlug: slug,
      ratingPercent: rating?.ratingPercent,
    });
    history.push(foodItemPath);
  };

  return (
    <StyledCarouselLineItem available={available} onClick={handleClick}>
      <LineItemImage
        src={imageUrl}
        width={IMAGE_SIZE}
        height={IMAGE_SIZE}
        sourceWidth={IMAGE_SIZE}
        sourceHeight={IMAGE_SIZE}
        alt={name}
      />
      <LineItemName>{name}</LineItemName>

      {ratingPercent === 0 || ratingPercent === 100 ? (
        <CarouselLineItemChips ratingPercent={ratingPercent} tags={tags} />
      ) : null}
    </StyledCarouselLineItem>
  );
};
